import * as React from 'react'
import * as ReactDOM from 'react-dom'
import moment from 'moment'
import { addMonths, format } from 'date-fns'
import { useNavigate, useParams } from 'react-router-dom'
import { useDate } from '@/modules/drivers/stores/dateStore'
import MonthlyNavigation from '../components/navigations/MonthlyNavigation'
import MonthlyWrapper from '../components/charts/monthly/MonthlyWrapper'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import AddCountryCodeModal from '../modals/AddCountryCodeModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'

const Monthly = () => {
  const {
    companyId,
    driverId,
    month: currentDate,
  } = useParams<{
    companyId: string
    driverId: string
    month: string
  }>()
  const navigate = useNavigate()
  const { t } = useLocalize()
  const can = usePermissionChecker(companyId)
  const canAddCountryCode = can('canEditActivities')

  const { setMonth } = useDate()
  const { setType } = useActivity()

  const ribbonRef = React.useRef(null)
  const [weekOffset, setWeekOffset] = React.useState(0)
  const wheelThrottleRef = React.useRef(false)
  const [isAddCountryCodeModalOpen, setIsAddCountryCodeModalOpen] =
    React.useState(false)

  const [contextMenu, setContextMenu] = React.useState<{
    x: number
    y: number
  } | null>(null)

  const [clickedDateTime, setClickedDateTime] = React.useState<string | null>(
    null,
  )
  const contextMenuRef = React.useRef<HTMLDivElement>(null)

  const computeDateTimeAt = (clientX: number, clientY: number): string => {
    const ribbon = ribbonRef.current as HTMLDivElement | null
    const rect = ribbon?.getBoundingClientRect()
    const [yearStr, monthStr] = (currentDate ?? '').split('-')
    const year = Number(yearStr)
    const monthIdx = Number(monthStr) - 1
    const monthStart = moment([year || moment().year(), monthIdx || 0, 1])
    const isoWeekday = monthStart.isoWeekday()
    const visibleStart =
      isoWeekday === 7
        ? monthStart.clone().subtract(1, 'day').startOf('isoWeek')
        : monthStart.clone().startOf('isoWeek')
    visibleStart.add(weekOffset, 'weeks')

    if (!rect) {
      return visibleStart.clone().hour(12).minute(0).format('YYYY-MM-DD HH:mm')
    }

    const HEADER_OFFSET_PX = 80
    const usableHeight = Math.max(rect.height - HEADER_OFFSET_PX, 1)
    const yRel = Math.min(
      Math.max(clientY - rect.top - HEADER_OFFSET_PX, 0),
      usableHeight - 1,
    )
    const dayIdx = Math.floor((yRel / usableHeight) * 42)
    const xRel = Math.min(
      Math.max(clientX - rect.left, 0),
      Math.max(rect.width - 1, 0),
    )
    const hour = Math.min(Math.floor((xRel / Math.max(rect.width, 1)) * 24), 23)

    return visibleStart
      .clone()
      .add(dayIdx, 'days')
      .hour(hour)
      .minute(0)
      .format('YYYY-MM-DD HH:mm')
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY })
    setClickedDateTime(computeDateTimeAt(e.clientX, e.clientY))
  }

  const closeContextMenu = React.useCallback(() => setContextMenu(null), [])

  React.useEffect(() => {
    if (!contextMenu) return undefined
    const handleClick = (e: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target as Node)
      ) {
        closeContextMenu()
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu()
    }
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick)
      document.addEventListener('keydown', handleKeyDown)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [contextMenu, closeContextMenu])

  const handleAddCountryCodeEntry = () => {
    if (!canAddCountryCode) return
    setIsAddCountryCodeModalOpen(true)
    closeContextMenu()
  }

  const goToMonth = (months: number) => {
    const [year, month] = currentDate.split('-').map(Number)
    const next = format(addMonths(new Date(year, month - 1), months), 'YYYY-MM')
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/monthlyActivity/${next}`,
    )
  }

  const goToDate = ({ months, weeks }: { months?: number; weeks?: number }) => {
    if (!currentDate) return

    if (weeks !== undefined) {
      const newWeekOffset = weekOffset + weeks

      if (Math.abs(newWeekOffset) >= 4) {
        const monthsToAdd = Math.floor(newWeekOffset / 4)
        const remainingWeeks = newWeekOffset % 4
        goToMonth(monthsToAdd)
        setWeekOffset(remainingWeeks)
      } else {
        setWeekOffset(newWeekOffset)
      }
    } else if (months !== undefined) {
      goToMonth(months)
      setWeekOffset(0)
    }
  }

  const handleWheel = React.useCallback(
    (e: React.WheelEvent) => {
      if (wheelThrottleRef.current) return

      wheelThrottleRef.current = true
      setTimeout(() => {
        wheelThrottleRef.current = false
      }, 200)

      if (e.deltaY > 0) {
        goToDate({ weeks: 1 })
      } else if (e.deltaY < 0) {
        goToDate({ weeks: -1 })
      }
    },
    [weekOffset, currentDate],
  )

  React.useEffect(() => {
    setType('monthly')
  }, [])

  React.useEffect(() => {
    setMonth(currentDate)
  }, [currentDate])

  React.useEffect(() => {
    setWeekOffset(0)
  }, [])

  return (
    <div
      ref={ribbonRef}
      className="tw-flex-1 tw-overflow-y-auto tw-bg-white"
      id="ribbon"
      style={{ position: 'relative' }}
      onContextMenu={handleContextMenu}
    >
      <MonthlyNavigation
        goToDate={({ weeks }) => goToDate({ weeks })}
        currentDate={currentDate}
      />
      <MonthlyWrapper
        ribbonRef={ribbonRef}
        currentDate={currentDate}
        weekOffset={weekOffset}
        companyId={companyId}
        driverId={Number(driverId)}
        onWheel={handleWheel}
      />

      {contextMenu &&
        ReactDOM.createPortal(
          <div
            ref={contextMenuRef}
            className="react-contextmenu react-contextmenu--visible"
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: 99999,
              background: '#fff',
              borderRadius: 4,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              padding: '4px 0',
              minWidth: 220,
            }}
          >
            <button
              type="button"
              disabled={!canAddCountryCode}
              className="custom-root"
              style={{
                padding: '6px 12px',
                cursor: canAddCountryCode ? 'pointer' : 'default',
                fontSize: 13,
                whiteSpace: 'nowrap',
                userSelect: 'none',
                opacity: canAddCountryCode ? 1 : 0.5,
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 0,
                background: 'transparent',
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleAddCountryCodeEntry()
              }}
              onMouseEnter={(e) => {
                ;(e.currentTarget as HTMLElement).style.background = '#f0f0f0'
              }}
              onMouseLeave={(e) => {
                ;(e.currentTarget as HTMLElement).style.background =
                  'transparent'
              }}
            >
              {t('countryCode.add-entry')}
            </button>
          </div>,
          document.body,
        )}
      {isAddCountryCodeModalOpen && (
        <AddCountryCodeModal
          companyId={companyId}
          driverId={driverId}
          defaultDateTime={clickedDateTime}
          handleClose={() => setIsAddCountryCodeModalOpen(false)}
        />
      )}
    </div>
  )
}

export default Monthly
